import { Component } from "react"

/* Modal */
import Modal from "react-modal"

/* Components */
import { Icon } from "../../components"

/* Icons */
import { LoadingOutlined } from "@ant-design/icons"

/* REST API */
import { get, stage, note, next, assign, link, organizations } from "../../api/Lead"

/* Widgets */
import { toast } from "../common"

/* Shared */
import { STAGES, REASONS, NICHES, NEXT_CHOICES, stageOf, sourceOf, nextOf, titleOf } from "./shared"
import { ago, initialsOf, avatarColorOf, digitsOf } from "../user/shared"
import { plural } from "../category/shared"

/* Moment */
import moment from "moment"

Modal.setAppElement("#root")


/* The states of a subscription in the words of the sales */
const SUBSCRIPTIONS = {
    active: { text: "Оплачена", accent: "green" },
    past_due: { text: "Продление не прошло", accent: "red" },
    pending: { text: "Начал оплату", accent: "orange" },
    cancelled: { text: "Отменена", accent: "grey" },
    expired: { text: "Истекла", accent: "grey" }
}

/* The roles of a team */
const ROLES = { owner: "Владелец", admin: "Администратор", specialist: "Специалист" }


/* Widget Lead Modal — one lead of the pipeline: who it is, where it stands, what the team said to it
   and what it became in Bookify. Everything a salesman does with a lead is done here: a call with
   the next date, a stage, the responsible, the point it became.
   Opened by the reference: this._lead.current.open(lead).
   Props: admins, onChanged — the board reloads, onEdit(lead), onOrganization(id) */
class LeadModal extends Component {

    constructor() {
        super()

        this.state = {
            visible: false,
            status: "loading",
            item: null,
            data: null,
            busy: false,

            /* The composer */
            type: "call",
            text: "",
            nextKey: "",
            nextCustom: "",

            /* The no */
            losing: false,
            reason: "",

            /* The search of the point */
            linking: false,
            search: "",
            found: []
        }

        this._timer = null
    }

    componentWillUnmount = () => {
        clearTimeout(this._timer)
    }

    /* losing — the lead was dropped on «Отказ» and the card opens asking why */
    open = (item, losing = false) => {
        this.setState({
            visible: true, status: "loading", item, data: null, busy: false,
            type: "call", text: "", nextKey: "", nextCustom: "",
            losing, reason: "", linking: false, search: "", found: []
        }, () => this.load())
    }

    close = () => {
        this.setState({ visible: false })
    }

    load = () => {

        /* Fields */
        const { item } = this.state

        get({ id: item.id }).then(response => {
            if (response && response.status === 200) {
                this.setState({ status: "ok", data: response.data, item: response.data.lead })
            }
            else {
                this.setState({ status: "error" })
            }
        }).catch(() => this.setState({ status: "error" }))
    }

    /* Every action reloads the card and tells the board */
    run = (request, success) => {

        /* Fields */
        const { onChanged } = this.props

        if (this.state.busy) {
            return Promise.resolve(false)
        }

        this.setState({ busy: true })

        return request.then(response => {

            this.setState({ busy: false })

            if (response && response.status === 200) {
                if (success) {
                    toast.success(success)
                }
                this.load()
                if (onChanged) {
                    onChanged()
                }
                return true
            }

            if (response && response.data && response.data.code === "ORGANIZATION_TAKEN") {
                toast.error(`Эта точка уже стоит за лидом #${response.data.id}`)
                return false
            }

            toast.error("Что-то пошло не так! Попробуйте позднее")
            return false
        }).catch(() => {
            this.setState({ busy: false })
            toast.error("Что-то пошло не так! Попробуйте позднее")
            return false
        })
    }

    copy = text => {

        if (!navigator.clipboard) {
            return
        }

        navigator.clipboard.writeText(text).then(() => toast.success("Скопировано")).catch(() => { })
    }

    /* The point goes to its own page — the card closes first */
    organization = id => {

        const { onOrganization } = this.props

        if (!onOrganization) {
            return
        }

        this.setState({ visible: false })
        onOrganization(id)
    }

    edit = () => {

        /* Fields */
        const { onEdit } = this.props
        const { item } = this.state

        this.setState({ visible: false })
        onEdit(item)
    }

    moveTo = key => {

        /* Fields */
        const { item } = this.state

        if (key === "lost") {
            this.setState({ losing: true, reason: "" })
            return
        }

        if (key === item.stage) {
            return
        }

        this.run(stage({ id: item.id, stage: key }), `Этап: ${stageOf(key).short}`)
    }

    lose = () => {

        /* Fields */
        const { item, reason } = this.state

        if (!reason) {
            toast.error("Выберите причину")
            return
        }

        this.run(stage({ id: item.id, stage: "lost", lostReason: reason }), "Лид в отказе").then(done => {
            if (done) {
                this.setState({ losing: false, reason: "" })
            }
        })
    }

    /* The date the composer will save as the next call: a quick choice or a typed one */
    nextDate = () => {

        /* Fields */
        const { nextKey, nextCustom } = this.state

        if (nextKey === "custom") {
            return nextCustom ? moment(nextCustom).toISOString() : undefined
        }

        if (nextKey === "none") {
            return null
        }

        const choice = NEXT_CHOICES.find(entry => entry.key === nextKey)

        if (choice) {
            return choice.date().toISOString()
        }

        return undefined
    }

    write = () => {

        /* Fields */
        const { item, type, text } = this.state

        if (type === "note" && text.trim().length === 0) {
            toast.error("Напишите заметку")
            return
        }

        const payload = { id: item.id, type, text: text.trim() }
        const nextAt = this.nextDate()

        if (nextAt !== undefined) {
            payload.nextAt = nextAt
        }

        this.run(note(payload), type === "call" ? "Звонок записан" : "Заметка сохранена").then(done => {
            if (done) {
                this.setState({ text: "", nextKey: "", nextCustom: "" })
            }
        })
    }

    searchOrganizations = text => {

        clearTimeout(this._timer)
        this.setState({ search: text })

        if (text.trim().length < 2) {
            this.setState({ found: [] })
            return
        }

        this._timer = setTimeout(() => {
            organizations({ search: text }).then(response => {
                if (response && response.status === 200) {
                    this.setState({ found: response.data.list })
                }
            }).catch(() => { })
        }, 350)
    }

    linkTo = organizationID => {

        /* Fields */
        const { item } = this.state

        this.run(link({ id: item.id, organizationID }), organizationID ? "Точка привязана" : "Точка отвязана").then(done => {
            if (done) {
                this.setState({ linking: false, search: "", found: [] })
            }
        })
    }

    /* The top — who it is and how to reach them */
    _hero = () => {

        /* Fields */
        const { item } = this.state

        const { title, subtitle } = titleOf(item)
        const source = sourceOf(item.source)
        const current = stageOf(item.stage)
        const phone = digitsOf(item.phone)

        return (
            <div className="user-hero" style={{ "--avatar": avatarColorOf(item.id) }}>

                <div className="lead-hero-avatar">
                    <span>{initialsOf(title)}</span>
                </div>

                <div className="user-hero-text">

                    <div className="user-hero-name">{title}</div>
                    {subtitle && <div className="lead-hero-person">{subtitle}</div>}

                    <div className="user-hero-badges">
                        <div className={`user-badge ${current.accent}`}><Icon name={current.icon} size={12} width={2.2} />{current.short}</div>
                        <div className={`user-badge ${source.accent}`} title={source.hint}><Icon name={source.icon} size={12} width={2.2} />{source.text}</div>
                        {item.niche && NICHES[item.niche] && <div className="user-badge grey">{NICHES[item.niche]}</div>}
                        {item.city && <div className="user-badge grey">{item.city}</div>}
                    </div>

                    <div className="user-hero-contacts">

                        {item.phone &&
                            <div onClick={() => this.copy(item.phone)} className="user-contact" title="Скопировать">
                                <Icon name="otp" size={14} width={2} />
                                {item.phone}
                            </div>
                        }

                        {phone.length >= 10 &&
                            <a href={`tel:+${phone}`} className="user-contact" title="Позвонить">
                                <Icon name="phone" size={14} width={2} />
                                Позвонить
                            </a>
                        }

                        {phone.length >= 10 &&
                            <a href={`https://wa.me/${phone}`} target="_blank" rel="noreferrer" className="user-contact whatsapp" title="Написать в WhatsApp">
                                <Icon name="support" size={14} width={2} />
                                WhatsApp
                            </a>
                        }

                        <div onClick={this.edit} className="user-contact" title="Изменить данные">
                            <Icon name="edit" size={14} width={2} />
                            Изменить
                        </div>

                    </div>

                </div>

            </div>
        )
    }

    /* The way of the lead — a step is pressed to move it */
    _stages = () => {

        /* Fields */
        const { item, losing, reason, busy } = this.state

        const index = STAGES.findIndex(entry => entry.key === item.stage)

        return (
            <div className="user-section">

                <div className={`lead-steps ${item.stage === "lost" ? "lost" : ""}`}>
                    {STAGES.filter(entry => entry.key !== "lost").map((entry, position) =>
                        <div
                            key={entry.key}
                            onClick={() => this.moveTo(entry.key)}
                            className={`lead-step ${entry.accent} ${item.stage === entry.key ? "current" : ""} ${item.stage !== "lost" && position < index ? "passed" : ""}`}
                            title={entry.hint}
                        >
                            <i>{item.stage !== "lost" && position < index ? <Icon name="check" size={12} width={3} /> : position + 1}</i>
                            <span>{entry.short}</span>
                        </div>
                    )}
                    <div onClick={() => this.moveTo("lost")} className={`lead-step lost ${item.stage === "lost" ? "current" : ""}`} title="Отказ">
                        <i><Icon name="close" size={10} width={3} /></i>
                        <span>{item.stage === "lost" && item.lostReason && REASONS[item.lostReason] ? REASONS[item.lostReason] : "Отказ"}</span>
                    </div>
                </div>

                {losing &&
                    <div className="lead-lose">
                        <b>Почему отказ?</b>
                        <div className="lead-chips">
                            {Object.keys(REASONS).map(key =>
                                <div key={key} onClick={() => this.setState({ reason: key })} className={`lead-chip ${reason === key ? "active" : ""}`}>{REASONS[key]}</div>
                            )}
                        </div>
                        <div className="lead-lose-actions">
                            <div onClick={() => this.setState({ losing: false })} className="lead-button light">Отмена</div>
                            <div onClick={this.lose} className={`lead-button danger ${busy ? "disabled" : ""}`}>В отказ</div>
                        </div>
                    </div>
                }

            </div>
        )
    }

    /* A call or a note, and when to come back */
    _composer = () => {

        /* Fields */
        const { item, type, text, nextKey, nextCustom, busy } = this.state

        if (item.stage === "won" || item.stage === "lost") {
            return null
        }

        const due = nextOf(item.nextAt)

        return (
            <div className="user-section">

                <div className="user-section-title">
                    Записать
                    {due && <span className={`lead-due ${due.accent}`}><Icon name="clock" size={12} width={2.2} />Звонок {due.text}</span>}
                </div>

                <div className="lead-composer">

                    <div className="lead-composer-types">
                        <div onClick={() => this.setState({ type: "call" })} className={type === "call" ? "active" : ""}><Icon name="phone" size={14} width={2} />Звонок</div>
                        <div onClick={() => this.setState({ type: "note" })} className={type === "note" ? "active" : ""}><Icon name="edit" size={14} width={2} />Заметка</div>
                    </div>

                    <textarea
                        rows={3}
                        maxLength={2000}
                        value={text}
                        placeholder={type === "call" ? "О чём договорились — можно пусто, если не дозвонились" : "Что важно знать о лиде"}
                        onChange={event => this.setState({ text: event.target.value })}
                    />

                    <div className="lead-composer-next">
                        <span>Перезвонить</span>
                        {NEXT_CHOICES.map(choice =>
                            <div key={choice.key} onClick={() => this.setState({ nextKey: nextKey === choice.key ? "" : choice.key })} className={`lead-chip small ${nextKey === choice.key ? "active" : ""}`}>{choice.text}</div>
                        )}
                        <div onClick={() => this.setState({ nextKey: nextKey === "custom" ? "" : "custom" })} className={`lead-chip small ${nextKey === "custom" ? "active" : ""}`}>
                            <Icon name="calendar" size={12} width={2} />Дата
                        </div>
                        {item.nextAt &&
                            <div onClick={() => this.setState({ nextKey: nextKey === "none" ? "" : "none" })} className={`lead-chip small ${nextKey === "none" ? "active" : ""}`}>Снять звонок</div>
                        }
                    </div>

                    {nextKey === "custom" &&
                        <input className="lead-datetime" type="datetime-local" value={nextCustom} min={moment().format("YYYY-MM-DDTHH:mm")} onChange={event => this.setState({ nextCustom: event.target.value })} />
                    }

                    <div className="lead-composer-footer">
                        <span>
                            {type === "call" && item.stage === "new" ? "Звонок переведёт лид в «Связались»" : ""}
                        </span>
                        <div onClick={this.write} className={`lead-button ${busy ? "disabled" : ""}`}>
                            {busy ? <LoadingOutlined /> : (type === "call" ? "Записать звонок" : "Сохранить")}
                        </div>
                    </div>

                </div>

            </div>
        )
    }

    /* Who leads it and the dates */
    _facts = () => {

        /* Fields */
        const { item, busy } = this.state
        const { admins } = this.props

        const due = nextOf(item.nextAt)

        return (
            <div className="user-section">

                <div className="user-section-title">Ответственный</div>

                <div className="lead-chips">
                    {(admins || []).map(admin =>
                        <div
                            key={admin.id}
                            onClick={() => !busy && this.run(assign({ id: item.id, adminID: parseInt(item.adminID) === parseInt(admin.id) ? null : admin.id }))}
                            className={`lead-chip person ${parseInt(item.adminID) === parseInt(admin.id) ? "active" : ""}`}
                        >
                            <i style={{ background: avatarColorOf(admin.id) }}>{initialsOf(admin.name)}</i>
                            {admin.name}{admin.me ? " · вы" : ""}
                        </div>
                    )}
                </div>

                <div className="user-facts lead-facts">
                    <div>
                        <span>Следующий звонок</span>
                        <b className={due ? `lead-due-text ${due.accent}` : ""}>
                            {due ? due.text : "не назначен"}
                            {item.nextAt && item.stage !== "won" && item.stage !== "lost" &&
                                <em onClick={() => !busy && this.run(next({ id: item.id, nextAt: null }), "Звонок снят")} title="Снять">×</em>
                            }
                        </b>
                    </div>
                    <div><span>В воронке с</span><b>{moment(item.createdAt).format("D MMMM YYYY")} · {ago(item.createdAt)}</b></div>
                    <div><span>Первый контакт</span><b>{item.contactedAt ? `${moment(item.contactedAt).format("D MMM, HH:mm")} · через ${moment(item.contactedAt).from(moment(item.createdAt), true)}` : "ещё не было"}</b></div>
                    <div><span>Звонков и заметок</span><b>{item.touches}</b></div>
                    {item.closedAt && <div><span>{item.stage === "won" ? "Подключён" : "Закрыт"}</span><b>{moment(item.closedAt).format("D MMMM YYYY")}</b></div>}
                </div>

                {item.comment &&
                    <div className="lead-comment">
                        <Icon name="support" size={14} width={2} />
                        <span>{item.comment}</span>
                    </div>
                }

            </div>
        )
    }

    /* What the lead already is in Bookify — the account in Pro and the point */
    _bookify = () => {

        /* Fields */
        const { data, item, linking, search, found, busy } = this.state

        const { member, organization } = data
        const subscription = organization && organization.subscriptionStatus ? SUBSCRIPTIONS[organization.subscriptionStatus] : null

        return (
            <div className="user-section">

                <div className="user-section-title">В Bookify</div>

                <div className="lead-bookify">

                    <div className={`lead-bookify-card ${member ? "" : "empty"}`}>
                        <div className="lead-bookify-icon violet"><Icon name="master" size={18} width={2} /></div>
                        <div className="lead-bookify-text">
                            {member
                                ? <>
                                    <b>{member.name ? member.name : "Без имени"} в Bookify Pro</b>
                                    <span>
                                        {parseInt(member.verified) === 1 ? "вошёл" : "код не ввёл"} {moment(member.createdAt).format("D MMM YYYY")}
                                        {member.teams.length > 0
                                            ? ` · ${member.teams.filter(team => parseInt(team.active) === 1).map(team => `${ROLES[team.role] ? ROLES[team.role].toLowerCase() : team.role} в «${team.name}»`).join(", ") || "ушёл из команд"}`
                                            : " · без точки"
                                        }
                                    </span>
                                </>
                                : <>
                                    <b>В Pro не входил</b>
                                    <span>Номер не стоит за аккаунтом мастера — свяжется сам, как только войдёт</span>
                                </>
                            }
                        </div>
                    </div>

                    {organization
                        ? <div className="lead-bookify-card link" onClick={() => this.organization(organization.id)}>
                            <div className="lead-bookify-icon orange"><Icon name="organization" size={18} width={2} /></div>
                            <div className="lead-bookify-text">
                                <b>{organization.name}</b>
                                <span>
                                    {organization.city ? `${organization.city} · ` : ""}
                                    {organization.team} {plural(organization.team, "мастер", "мастера", "мастеров")} · {organization.bookings30} {plural(organization.bookings30, "запись", "записи", "записей")} за 30 дней
                                </span>
                            </div>
                            <div className={`user-badge ${subscription ? subscription.accent : "grey"}`}>
                                {subscription ? subscription.text : "Не платил"}
                                {organization.subscriptionStatus === "active" && organization.currentPeriodEnd ? ` до ${moment(organization.currentPeriodEnd).format("D MMM")}` : ""}
                            </div>
                            <em onClick={event => { event.stopPropagation(); !busy && this.linkTo(null) }} title="Отвязать">×</em>
                        </div>
                        : <div className="lead-bookify-card empty">
                            <div className="lead-bookify-icon orange"><Icon name="organization" size={18} width={2} /></div>
                            <div className="lead-bookify-text">
                                <b>Точки нет</b>
                                <span>Как только точка оплатит подписку, лид сам встанет в «Подключены»</span>
                            </div>
                            {!linking && <div onClick={() => this.setState({ linking: true })} className="lead-button light small">Привязать</div>}
                        </div>
                    }

                    {linking && !organization &&
                        <div className="lead-link">
                            <div className="organization-search">
                                <div className="organization-search-icon"><Icon name="search" size={16} /></div>
                                <input autoFocus placeholder="Название точки или телефон" value={search} onChange={event => this.searchOrganizations(event.target.value)} />
                                <div onClick={() => this.setState({ linking: false, search: "", found: [] })} className="organization-search-clear">
                                    <Icon name="close" size={11} width={2.4} />
                                </div>
                            </div>
                            {found.map(entry =>
                                <div key={entry.id} onClick={() => !entry.leadID && this.linkTo(entry.id)} className={`lead-link-row ${entry.leadID && parseInt(entry.leadID) !== parseInt(item.id) ? "taken" : ""}`}>
                                    <b>{entry.name}</b>
                                    <span>{[entry.city, entry.phone].filter(value => value).join(" · ")}</span>
                                    {entry.leadID && <em>уже лид #{entry.leadID}</em>}
                                </div>
                            )}
                            {search.trim().length >= 2 && found.length === 0 && <div className="lead-link-empty">Ничего не нашлось</div>}
                        </div>
                    }

                    {data.twins.length > 0 &&
                        <div className="lead-twins">
                            <Icon name="alert" size={14} width={2} />
                            <span>
                                Этот номер есть ещё в {data.twins.length} {plural(data.twins.length, "лиде", "лидах", "лидах")}:{" "}
                                {data.twins.map(twin => `#${twin.id} ${stageOf(twin.stage).short.toLowerCase()}`).join(", ")}
                            </span>
                        </div>
                    }

                </div>

            </div>
        )
    }

    /* One step of the history in words */
    eventText = event => {

        if (event.type === "stage") {
            const from = event.fromStage ? stageOf(event.fromStage).short : "—"
            const to = stageOf(event.toStage).short
            return { icon: stageOf(event.toStage).icon, accent: stageOf(event.toStage).accent, title: `${from} → ${to}`, text: event.text }
        }

        if (event.type === "call") {
            return { icon: "phone", accent: "violet", title: "Звонок", text: event.text }
        }

        if (event.type === "note") {
            return { icon: "edit", accent: "blue", title: "Заметка", text: event.text }
        }

        if (event.type === "assign") {
            return { icon: "profile", accent: "grey", title: event.text ? `Ответственный: ${event.text}` : "Ответственный снят", text: null }
        }

        if (event.type === "next") {
            return { icon: "clock", accent: "orange", title: event.text ? `Перезвонить ${moment(event.text).format("D MMM, HH:mm")}` : "Звонок снят", text: null }
        }

        if (event.type === "link") {
            return { icon: "organization", accent: "orange", title: event.text ? `Привязана точка «${event.text}»` : "Точка отвязана", text: null }
        }

        if (event.type === "create") {
            return { icon: "sparkle", accent: "blue", title: "Лид появился", text: event.text }
        }

        return { icon: "sparkle", accent: "green", title: event.text, text: null }
    }

    _history = () => {

        /* Fields */
        const { data } = this.state

        return (
            <div className="user-section">

                <div className="user-section-title">История<span>{data.events.length}</span></div>

                <div className="lead-history">
                    {data.events.map(event => {

                        const view = this.eventText(event)

                        return (
                            <div key={event.id} className={`lead-event ${view.accent}`}>
                                <div className="lead-event-icon"><Icon name={view.icon} size={13} width={2.2} /></div>
                                <div className="lead-event-body">
                                    <div className="lead-event-title">
                                        <b>{view.title}</b>
                                        <span>{event.admin ? event.admin : "Система"} · {moment(event.createdAt).format("D MMM, HH:mm")}</span>
                                    </div>
                                    {view.text && <div className="lead-event-text">{view.text}</div>}
                                </div>
                            </div>
                        )
                    })}
                </div>

            </div>
        )
    }

    _body = () => {

        /* Fields */
        const { status } = this.state

        if (status === "loading") {
            return <div className="wide-modal-loading"><LoadingOutlined /></div>
        }

        if (status !== "ok") {
            return (
                <div className="wide-modal-loading">
                    <Icon name="alert" size={38} width={1.5} />
                    <p>Не удалось загрузить лид</p>
                </div>
            )
        }

        return (
            <>
                {this._stages()}
                {this._composer()}
                {this._facts()}
                {this._bookify()}
                {this._history()}
            </>
        )
    }

    render = () => {

        /* Fields */
        const { visible, item } = this.state

        return (
            <Modal
                isOpen={visible}
                onRequestClose={this.close}
                className="wide-modal large user-modal lead-modal"
                overlayClassName="wide-modal-overlay"
                closeTimeoutMS={200}
            >

                <div className="wide-modal-header">
                    <h2>Лид{item ? ` #${item.id}` : ""}</h2>
                    <div onClick={this.close} className="wide-modal-close">
                        <Icon name="close" size={11} width={2.2} />
                    </div>
                </div>

                {item &&
                    <div className="wide-modal-body">
                        {this._hero()}
                        {this._body()}
                    </div>
                }

            </Modal>
        )
    }
}

export default LeadModal
